<script setup>
import { useI18n } from 'vue-i18n';
import { createRealEstateBrokerInvestmentPresenter } from '../composables/realEstateBrokerInvestmentPresenter';

defineProps({
  loading: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['open']);
const { t } = useI18n();
const {
  address,
  addressDetail,
  date,
  money,
  temperatureClass,
  temperatureIcon,
  temperatureLabel,
} = createRealEstateBrokerInvestmentPresenter(t);
</script>

<template>
  <div
    class="hidden overflow-x-auto rounded-lais border border-lais-border-soft bg-white shadow-lais-card lg:block"
  >
    <table class="w-full min-w-[880px] text-left text-sm">
      <thead
        class="border-b border-lais-border-soft bg-lais-bg-soft text-xs uppercase tracking-wide text-lais-text-muted"
      >
        <tr>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.STATUS') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.VALUE') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.ADDRESS') }}
          </th>
          <th class="p-4">
            {{ t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.UPDATED_AT') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-lais-border-soft">
        <tr v-for="index in loading ? 3 : 0" :key="`loading-${index}`">
          <td colspan="4" class="p-5">
            <div class="h-5 animate-pulse rounded bg-lais-bg-soft" />
          </td>
        </tr>
        <tr v-if="!loading && !rows.length">
          <td colspan="4" class="p-8 text-center text-lais-text-muted">
            {{ t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.EMPTY') }}
          </td>
        </tr>
        <tr
          v-for="row in loading ? [] : rows"
          :key="row.imovel_id"
          class="cursor-pointer hover:bg-lais-bg-soft/60"
          tabindex="0"
          @click="emit('open', row.imovel_id)"
          @keydown.enter="emit('open', row.imovel_id)"
          @keydown.space.prevent="emit('open', row.imovel_id)"
        >
          <td class="p-4">
            <span
              class="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-semibold"
              :class="temperatureClass(row.temperatura_calculada)"
            >
              <span
                class="size-3.5"
                :class="temperatureIcon(row.temperatura_calculada)"
              />
              {{ temperatureLabel(row.temperatura_calculada) }}
            </span>
          </td>
          <td class="p-4 font-semibold text-lais-text">
            {{ money(row.valor_anunciado) }}
          </td>
          <td class="p-4">
            <div class="flex items-center gap-3">
              <img
                v-if="row.imagem"
                :src="row.imagem"
                :alt="t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.IMAGE_ALT')"
                class="size-10 rounded-full object-cover"
              />
              <span
                v-else
                class="i-lucide-image size-10 rounded-full bg-lais-bg-soft p-2 text-lais-text-muted"
              />
              <div>
                <p class="font-semibold text-lais-text">{{ address(row) }}</p>
                <p
                  v-if="addressDetail(row)"
                  class="text-xs text-lais-text-muted"
                >
                  {{ addressDetail(row) }}
                </p>
              </div>
            </div>
          </td>
          <td class="p-4 text-lais-text-body">{{ date(row.atualizado_em) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
